import React from "react";
import PhoneInput, {
  getCountries,
  type Country,
} from "react-phone-number-input";
import phoneCountryLabels from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";

// import logo from "../../../assets/images/icons/logo.png";
import regimg from "../../../assets/images/pages/registerpageimage.png";
// component
import { Divider } from "antd";
import TextInput from "../../../components/elements/form/TextInput";
import Button from "../../../components/elements/form/Button";

// navigator
import { Link } from "react-router-dom";

// constants
import useRegister from "../hooks/useRegister";
import CheckBox from "../../../components/elements/form/CheckBox";
import { FaCheck, FaGoogle } from "react-icons/fa";
// import { googleLogout, useGoogleLogin } from "@react-oauth/google";
import { useGoogleLogin } from "@react-oauth/google";
// import { get_google_profile } from "../../../service/api/auth";
import AnimatedPage from "../../../components/animation/AnimatedPage";
import { useAppSelector } from "../../../store";
import SelectInput from "../../../components/elements/form/SelectInput";
import { useAppDispatch } from "../../../store";
import { get_countries } from "../../../service/api/locations";

const normalizeCountryName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const phoneCountryByName = new Map<string, Country>(
  getCountries().map((country) => [
    normalizeCountryName(phoneCountryLabels[country] || ""),
    country,
  ]),
);

const getPhoneCountry = (countryName: string): Country | undefined =>
  phoneCountryByName.get(normalizeCountryName(countryName));

const Register = () => {
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  const loading = useAppSelector(({ loading }) => loading.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const locationsLoading = useAppSelector(({ locations }) => locations.loading);
  const {
    data,
    error,
    // token,
    tokenSet,
    errorSet,
    dataSet,
    onSubmit,
    onChange,
  } = useRegister();
  const selectedCountry = countries.find(
    (country) => country.id === data.country_id,
  );
  const phoneDefaultCountry = selectedCountry
    ? getPhoneCountry(selectedCountry.name)
    : undefined;

  React.useEffect(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  const login = useGoogleLogin({
    onSuccess: (res) => tokenSet(res?.access_token),
    onError: (error) => console.log("Login Failed:", error),
  });

  // const logOut = () => {
  //   googleLogout();
  // };

  // const onLoad = React.useCallback(() => {
  // if (token) {
  //   get_google_profile(token, dispatch, navigate);
  //   tokenSet("");
  //   logOut();
  // }
  // }, []);

  return (
    <AnimatedPage>
      <main className="min-h-screen w-full bg-customgreen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <section className="max-w-7xl w-full overflow-hidden rounded-2xl shadow-xl bg-white">
          <div className="grid md:grid-cols-2">
            {/* Left Column Form/> */}
            <div className="w-full space-y-8 p-8 sm:p-12 lg:p-16">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-primary-900 sm:text-3xl mb-2">
                  Register with Us!
                </h2>
                <p className="max-w-2xl mx-auto text-sm text-primary-600">
                  Already Have an account?
                  <Link
                    to="/login"
                    className="ml-2 font-medium text-bggreen hover:text-midnight-200"
                  >
                    Sign in
                  </Link>
                </p>
              </div>

              <form className="space-y-6 relative">
                <SelectInput
                  label="Select Country"
                  name="country_id"
                  items={countries.map((country) => ({
                    id: String(country.id),
                    name: country.name,
                  }))}
                  value={data.country_id || ""}
                  loading={locationsLoading}
                  onChange={(event) => {
                    const countryId = Number(event.target.value);
                    dataSet((prev) => ({
                      ...prev,
                      country_id: countryId,
                      phone_number:
                        prev.country_id === countryId ? prev.phone_number : "",
                    }));
                    errorSet((prev) => ({ ...prev, country_id: "" }));
                  }}
                  status={error.country_id}
                />

                <TextInput
                  type="text"
                  label="Full Name"
                  name="name"
                  value={data.name}
                  placeholder="Full Name"
                  required={true}
                  onChange={onChange}
                  status={error.name}
                />

                <TextInput
                  type="text"
                  label="Email"
                  name="email"
                  value={data.email}
                  placeholder="Email"
                  required={true}
                  onChange={onChange}
                  status={error.email}
                />

                {/* <TextInput
                  type="text"
                  label="Address"
                  name="address"
                  value={data.address}
                  placeholder="Address"
                  required={true}
                  onChange={onChange}
                  status={error.address}
                /> */}

                <div className="w-full">
                  <label
                    htmlFor="phone_number"
                    className="text-md font-medium"
                  >
                    Phone Number
                  </label>
                  <div
                    className={`flex w-full h-11 border items-center rounded-lg mt-1 text-gray-900 px-3 py-2 focus-within:ring focus-within:ring-primary-100/30 ${
                      error.phone_number
                        ? "border-[red]"
                        : "border-[#e5e7eb] focus-within:border-primary-500"
                    }`}
                  >
                    <PhoneInput
                      key={`${data.country_id}-${phoneDefaultCountry || ""}`}
                      id="phone_number"
                      defaultCountry={phoneDefaultCountry}
                      international
                      countryCallingCodeEditable={false}
                      value={data.phone_number || undefined}
                      onChange={(value) => {
                        dataSet((prev) => ({
                          ...prev,
                          phone_number: value ?? "",
                        }));
                        errorSet((prev) => ({
                          ...prev,
                          phone_number: "",
                        }));
                      }}
                      numberInputProps={{
                        name: "phone_number",
                        required: true,
                        placeholder: "Phone Number",
                        className:
                          "PhoneInputInput w-full !outline-none px-0 py-0 text-sm border-none focus:border-transparent focus:ring-0",
                      }}
                      className="PhoneInput flex-1 gap-2"
                    />
                  </div>
                  {error.phone_number && (
                    <p className="mt-1 text-sm text-red-600">
                      Please enter a phone number.
                    </p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <TextInput
                    type="password"
                    label="Password"
                    name="password"
                    value={data.password}
                    placeholder="Password"
                    required={true}
                    onChange={onChange}
                    status={error.password}
                  />

                  <TextInput
                    type="password"
                    label="Confirm Password"
                    name="password_confirmation"
                    value={data.password_confirmation}
                    placeholder="Comfirm Password"
                    required={true}
                    onChange={onChange}
                    status={error.password_confirmation}
                  />
                </div>

                <div className="">
                  <CheckBox
                    name="accepted_terms"
                    value={data.accepted_terms}
                    status={error.accepted_terms}
                    onChange={() => {
                      dataSet((prev) => ({
                        ...prev,
                        accepted_terms:
                          data.accepted_terms === true ? false : true,
                      }));

                      errorSet((prev) => ({ ...prev, accepted_terms: "" }));
                    }}
                    label={`Accept our <a class="text-primary-500 hover:text-primary-900 cursor-pointer" href="/terms-and-condition" class="text-bggreen">Terms and Conditions</a>`}
                  />
                </div>
              </form>
              <div className="grid lg:grid-cols-2 gap-4">
                <Button
                  title="Sign Up"
                  type="button"
                  loading={loading}
                  onHandler={() => onSubmit()}
                  className="w-full text-white px-6 rounded-full"
                  bg_color="bg-primary-900"
                  bg_hoverColor="bg-primary-700"
                />

                <div className="lg:hidden">
                  <Divider>Or Sign Up With</Divider>
                </div>

                <Button
                  type="button"
                  title="Google"
                  titleColor="text-dark"
                  icon={<FaGoogle size={24} className="text-dark" />}
                  onHandler={() => login()}
                  className="w-full rounded-full mb-12 md:mb-0"
                  bg_color="bg-primary-50/20"
                  bg_hoverColor="bg-primary-400"
                  translateClassName="translate-x-full"
                />
              </div>
            </div>
            {/* Right Column - Image & Content */}
            <div className="relative hidden md:block bg-primary-800 m-6 rounded-xl overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary-700/30 blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-primary-600/20 blur-3xl"></div>
              </div>
              <div className="relative h-full flex flex-col justify-center p-12 z-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4 sm:text-3xl">
                    Welcome Back
                  </h2>
                  <p className="text-sm text-white">
                    Sign in to your account to access your dashboard and manage
                    your business services.
                  </p>
                </div>
                <div className="relative sm:h-64 lg:h-80 w-full">
                  <img
                    src={regimg || "/placeholder.svg"}
                    alt="Login illustration"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </div>
                <div className="mt-8">
                  <h3 className="text-white text-lg font-medium mb-4">
                    Why join us?
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Streamlined business registration process",
                      "Automated compliance reminders",
                      "Secure document storage and sharing",
                      "Expert support from our team",
                      "Regular updates on regulatory changes",
                    ].map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <FaCheck className="h-3 w-3 text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-white/70 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </AnimatedPage>
  );
};

export default Register;
